import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";

/** `group` is the category the child is listed under (see Sidebar's REPORT_GROUP). */
export type ChildMenuType = { title: string; link: string; group?: string };

/** A parent's icon on compact screens (Sidebar's iconSx); the tree lines up with it. */
export const COMPACT_ICON_SIZE = "1.2rem";

const compact = `@media ${COMPACT_QUERY}`;

/**
 * The tree hangs from a line down the centre of the parent's icon, and each child's label starts
 * where the parent's label does, so both come from the parent row: its padding (Sidebar's rowSx),
 * its icon (MUI's medium SvgIcon, whose size follows the theme's font scale; COMPACT_ICON_SIZE on
 * compact screens) and the gap after the icon. A category's reports hang one step further in,
 * from a line under the category's label.
 */
const PARENT_ROW_PADDING_PX = 12;
const PARENT_ICON_GAP_PX = 16;
const MEDIUM_ICON_PX = 24;

const TREE = {
  branchWidth: 14,
  hookGap: 4,
  hookRadius: 8,
  rowHeight: 40,
  rowGap: 2,
  nestedLineOffset: 6,
  nestedLabelInset: 10,
} as const;

/** Where the tree line and the child labels sit, for a given parent icon size. */
const treeGeometry = (iconSize: string) => ({
  "--tree-line-x": `calc(${PARENT_ROW_PADDING_PX}px + ${iconSize} / 2)`,
  "--tree-label-x": `calc(${PARENT_ROW_PADDING_PX + PARENT_ICON_GAP_PX}px + ${iconSize})`,
});

const treeSx = (theme: Theme) => ({
  ...treeGeometry(theme.typography.pxToRem(MEDIUM_ICON_PX)),
  [compact]: treeGeometry(COMPACT_ICON_SIZE),
});

const CHILD_LINE_X = "var(--tree-line-x)";
const NESTED_LINE_X = `calc(var(--tree-label-x) + ${TREE.nestedLineOffset}px)`;

/**
 * One branch hanging from the line at `lineX`: its row and anything opened under it. The line
 * runs on past every branch but the last, which ends it at its own row.
 */
const branchSx = (lineX: string, isLast: boolean) => ({
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: lineX,
    top: 0,
    ...(isLast ? { height: `${TREE.rowHeight / 2}px` } : { bottom: 0 }),
    borderLeft: "1px solid",
    borderColor: "divider",
  },
});

/** A tree row's size, plus the rounded hook that joins it to its line. */
const treeRowSx = {
  minHeight: `${TREE.rowHeight}px`,
  mb: `${TREE.rowGap}px`,
  py: 0,
  "&::before": {
    content: '""',
    position: "absolute",
    left: `-${TREE.branchWidth}px`,
    top: `calc(50% - ${TREE.hookRadius}px)`,
    width: `${TREE.branchWidth - TREE.hookGap}px`,
    height: `${TREE.hookRadius}px`,
    borderLeft: "1px solid",
    borderBottom: "1px solid",
    borderColor: "divider",
    borderBottomLeftRadius: `${TREE.hookRadius}px`,
  },
};

/** A row hanging from the parent's line: a child, or one of the parent's categories. */
const childRowSx = {
  ...treeRowSx,
  ml: `calc(${CHILD_LINE_X} + ${TREE.branchWidth}px)`,
  pl: `calc(var(--tree-label-x) - ${CHILD_LINE_X} - ${TREE.branchWidth}px)`,
};

/** A report inside an opened category, hanging from the category's own line. */
const nestedRowSx = {
  ...treeRowSx,
  ml: `calc(${NESTED_LINE_X} + ${TREE.branchWidth}px)`,
  pl: `${TREE.nestedLabelInset}px`,
};

/** A category's ">" turns down once its reports are showing. */
const chevronSx = {
  transition: (theme: Theme) => theme.transitions.create("transform"),
};
const openChevronSx = { ...chevronSx, transform: "rotate(90deg)" };

const labelSx = { fontSize: "0.9rem", fontWeight: 500 };
/** The entry of the page on screen; its colour comes from the row's highlight. */
const activeLabelSx = { ...labelSx, fontWeight: 600 };
/** A category holding the page on screen: no highlight of its own, but it reads as the path. */
const pathLabelSx = { ...activeLabelSx, color: "text.primary" };

/** A live count spelled out beside a row's label. */
const countPillSx = {
  px: 0.75,
  borderRadius: "999px",
  bgcolor: "error.main",
  color: "error.contrastText",
  fontSize: "0.7rem",
  fontWeight: 700,
  lineHeight: 1.6,
};

/** A run of children that share a group, shown as one category that opens onto them. */
type TreeCategory = { kind: "category"; group: string; children: ChildMenuType[] };

/** What the tree shows: ungrouped children as rows, grouped ones as categories. */
type TreeEntry = { kind: "leaf"; child: ChildMenuType } | TreeCategory;

const treeEntriesOf = (items: ChildMenuType[]): TreeEntry[] =>
  items.reduce<TreeEntry[]>((entries, child) => {
    const previous = entries[entries.length - 1];
    if (!child.group) return [...entries, { kind: "leaf", child }];
    if (previous?.kind === "category" && previous.group === child.group) {
      return [...entries.slice(0, -1), { ...previous, children: [...previous.children, child] }];
    }
    return [...entries, { kind: "category", group: child.group, children: [child] }];
  }, []);

const entryKey = (entry: TreeEntry): string =>
  entry.kind === "leaf" ? entry.child.title : `category:${entry.group}`;

const childKey = (child: ChildMenuType): string => child.title;

/**
 * Most-specific-wins: a shorter sibling path (e.g. /sales) must not stay highlighted when a
 * longer sibling (e.g. /sales/reprint-authorization) also matches the current path.
 */
const isChildActive = (pathname: string, child: ChildMenuType, siblings: ChildMenuType[]) => {
  const matches = (link: string) => pathname === link || pathname.startsWith(link + "/");
  return (
    matches(child.link) &&
    !siblings.some(
      (sibling) => sibling.link.length > child.link.length && matches(sibling.link),
    )
  );
};

/** The categories holding the page on screen, opened on top of whatever is open already. */
const withPageCategoryOpen = (
  open: Record<string, boolean>,
  pathname: string,
  items: ChildMenuType[],
): Record<string, boolean> => {
  const groups = items.flatMap((child) =>
    child.group && pathname.startsWith(child.link) ? [child.group] : [],
  );
  return groups.every((group) => open[group])
    ? open
    : { ...open, ...Object.fromEntries(groups.map((group) => [group, true])) };
};

interface SidebarChildTreeProps {
  items: ChildMenuType[];
  /** The sidebar's row style, which every tree row builds on. */
  rowSx: SystemStyleObject<Theme>;
  /** The sidebar's highlight for the entry of the page on screen. */
  activeSx: SystemStyleObject<Theme>;
  countForChild: (child: ChildMenuType) => number;
  /** Called when an entry is picked, e.g. to close the pop-out the tree is shown in. */
  onNavigate?: () => void;
}

/**
 * An expanded parent's children as a tree hanging from the parent's icon. Children that share a
 * group are gathered into a category row whose ">" opens onto them; the category holding the
 * page on screen starts open. Plain entries carry no ">".
 */
export default function SidebarChildTree({
  items,
  rowSx,
  activeSx,
  countForChild,
  onNavigate,
}: Readonly<SidebarChildTreeProps>) {
  const { pathname } = useLocation();
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(() =>
    withPageCategoryOpen({}, pathname, items),
  );

  useEffect(() => {
    setOpenCategories((prev) => withPageCategoryOpen(prev, pathname, items));
  }, [pathname, items]);

  const toggleCategory = (group: string) =>
    setOpenCategories((prev) => ({ ...prev, [group]: !prev[group] }));

  /** A row's label, with its live count spelled out beside it when there is one. */
  const renderRowLabel = (title: string, count: number) =>
    count > 0 ? (
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {title}
        {/* Spelled out beside the label rather than shown as a
            floating dot: this row is text in a list, and a count
            the reader has to hover to understand is not a warning. */}
        <Box component="span" sx={countPillSx}>
          {count}
        </Box>
      </Box>
    ) : (
      title
    );

  /** Each item as one branch of the tree hanging from the line at `lineX`. */
  const renderBranches = <T,>(
    branches: T[],
    lineX: string,
    renderItem: (item: T) => ReactNode,
    keyOf: (item: T) => string,
  ) =>
    branches.map((item, index) => (
      <Stack key={keyOf(item)} sx={branchSx(lineX, index === branches.length - 1)}>
        {renderItem(item)}
      </Stack>
    ));

  /** A link row: a parent's child, or a report inside an opened category. */
  const renderLeafRow = (child: ChildMenuType, levelSx: typeof childRowSx) => {
    const childActive = isChildActive(pathname, child, items);
    return (
      <ListItemButton
        component={Link}
        to={child.link}
        selected={childActive}
        sx={{ ...rowSx, ...levelSx, ...(childActive ? activeSx : {}) }}
        onClick={onNavigate}
      >
        <ListItemText
          primary={renderRowLabel(child.title, countForChild(child))}
          slotProps={{ primary: childActive ? activeLabelSx : labelSx }}
        />
      </ListItemButton>
    );
  };

  const renderReport = (child: ChildMenuType) => renderLeafRow(child, nestedRowSx);

  /** A category row that opens onto its reports, which hang from the category's own line. */
  const renderCategory = (category: TreeCategory) => {
    const isOpen = Boolean(openCategories[category.group]);
    const holdsActivePage = category.children.some((child) =>
      isChildActive(pathname, child, items),
    );
    // Closed, the category carries its reports' counts, as a closed parent does.
    const count = isOpen
      ? 0
      : category.children.reduce((total, child) => total + countForChild(child), 0);
    const handleToggle = () => toggleCategory(category.group);
    return (
      <>
        <ListItemButton
          aria-expanded={isOpen}
          onClick={handleToggle}
          sx={{ ...rowSx, ...childRowSx }}
        >
          <ListItemText
            primary={renderRowLabel(category.group, count)}
            slotProps={{ primary: holdsActivePage ? pathLabelSx : labelSx }}
          />
          <ChevronRightIcon fontSize="small" sx={isOpen ? openChevronSx : chevronSx} />
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {renderBranches(category.children, NESTED_LINE_X, renderReport, childKey)}
        </Collapse>
      </>
    );
  };

  const renderEntry = (entry: TreeEntry) =>
    entry.kind === "leaf" ? renderLeafRow(entry.child, childRowSx) : renderCategory(entry);

  return (
    <Box sx={treeSx}>
      {renderBranches(treeEntriesOf(items), CHILD_LINE_X, renderEntry, entryKey)}
    </Box>
  );
}
